import { useState, type ReactElement } from "react";

import { AppShell } from "../components/AppShell";
import { Tabs, type TabDef } from "../components/Tabs";
import { Onboarding } from "../components/dashboard/Onboarding";
import { AccountTab } from "../components/dashboard/AccountTab";
import { ConsentCopy } from "../components/dashboard/ConsentCopy";
import { PhotosTab } from "../components/dashboard/PhotosTab";
import { DailyLog } from "../components/dashboard/DailyLog";
import { StudyTasksTab } from "../components/dashboard/StudyTasksTab";
import { TrendsTab } from "../components/dashboard/TrendsTab";
import { DEFAULT_PROTOCOL_CONFIG } from "../features/dashboard/protocol";
import { useDashboard } from "../features/dashboard/useDashboard";
import { SkeletonRows } from "../components/Skeleton";
import { ErrorNotice } from "../components/ErrorNotice";

const TABS: TabDef[] = [
  { id: "account", label: "Account", icon: "fas fa-user" },
  { id: "consent", label: "Consent", icon: "fas fa-file-signature" },
  { id: "photos", label: "Photos", icon: "fas fa-camera" },
  { id: "daily", label: "Daily Log", icon: "fas fa-clipboard-list" },
  { id: "trends", label: "Trends", icon: "fas fa-chart-line" },
];

// Results-hidden studies (protocol results_visible=false, e.g. the NeuroAge
// competition) get a results-free portal: FaceAge is recorded but never shown,
// so the Daily log and Trends tabs (both of which display it) are hidden.
const RESULTS_FREE_TABS: TabDef[] = TABS.filter((t) => t.id !== "daily" && t.id !== "trends");

export function Dashboard(): ReactElement {
  const data = useDashboard();
  const [activeTab, setActiveTab] = useState("account");

  const active = data.profile?.enrollment_status === "active";
  const onboardingComplete = active && data.hasOneTime;
  // Fully config-driven: NeuroAge is its own parent study (0038) with a real
  // protocol row since migration 0014 — it is not special-cased anywhere.
  const config = data.protocol?.config ?? DEFAULT_PROTOCOL_CONFIG;
  const studyQuestionnaires = config.questionnaires ?? [];
  let tabs = config.results_visible ? TABS : RESULTS_FREE_TABS;
  if (studyQuestionnaires.length > 0) {
    // The study defines supplemental questionnaires — give them their own tab
    // right after Photos.
    const at = tabs.findIndex((t) => t.id === "photos") + 1;
    tabs = [
      ...tabs.slice(0, at),
      { id: "tasks", label: "Study tasks", icon: "fas fa-list-check" },
      ...tabs.slice(at),
    ];
  }

  // After a daily submission, refresh both the photos/trends and the daily
  // questionnaire list so the Daily log picks up the new entry.
  async function onSubmitted(): Promise<void> {
    await Promise.all([data.reloadResults(), data.reloadQuestionnaires()]);
  }

  return (
    <AppShell brand="FaceAge">
      {data.loading ? (
          <SkeletonRows rows={4} />
        ) : data.error ? (
          <ErrorNotice>{data.error}</ErrorNotice>
        ) : !onboardingComplete ? (
          <Onboarding
            profile={data.profile}
            protocol={data.protocol}
            reloadProfile={data.reloadProfile}
            reloadQuestionnaires={data.reloadQuestionnaires}
          />
        ) : (
          <>
            <Tabs tabs={tabs} active={activeTab} onSelect={setActiveTab} />
            {activeTab === "account" && data.profile ? (
              <AccountTab
                profile={data.profile}
                oneTime={data.questionnaires.find((q) => q.questionnaire_type === "one_time") ?? null}
                study={data.protocol?.study ?? null}
              />
            ) : null}
            {activeTab === "consent" && data.profile ? (
              <ConsentCopy profile={data.profile} />
            ) : null}
            {activeTab === "photos" ? (
              <PhotosTab
                onProcessed={onSubmitted}
                config={config}
                studyDay={data.protocol?.study_day ?? null}
                photos={data.photos}
              />
            ) : null}
            {activeTab === "tasks" ? (
              <StudyTasksTab
                specs={studyQuestionnaires}
                responses={data.questionnaires}
                studyDay={data.protocol?.study_day ?? null}
                onSubmitted={data.reloadQuestionnaires}
              />
            ) : null}
            {activeTab === "daily" ? (
              <DailyLog
                photos={data.photos}
                trends={data.trends}
                questionnaires={data.questionnaires}
              />
            ) : null}
            {activeTab === "trends" ? <TrendsTab trends={data.trends} /> : null}
          </>
        )}
    </AppShell>
  );
}
